import { when } from 'mobx';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Fundraiser, TFundraiserState } from '@feathr/blackbox';
import type { IFundraiserCRMIntegration } from '@feathr/blackbox/src/fundraisers';
import { showError } from '@feathr/components';

import { useStore } from '../state';

interface IUseFundraiserReturn {
  fundraiser: Fundraiser | undefined;
  isPending: boolean;
  state: TFundraiserState | undefined;
  devClearProject: () => Promise<void>;
  disconnectCRM: () => void;
  crm?: IFundraiserCRMIntegration;
}

/** Each account has a single Fundraiser document which is created when first navigating
 * to the Fundraise page.
 */
export default function useFundraiser(): IUseFundraiserReturn {
  const { t } = useTranslation();
  const { Fundraisers } = useStore();
  const [fundraiser, setFundraiser] = useState<Fundraiser | undefined>(undefined);

  async function fetchFundraiser(): Promise<void> {
    try {
      const response = await Fundraisers.getOrCreate();
      await when(() => !response.isPending);
      setFundraiser(response);
    } catch (error) {
      showError(error, t);
    }
  }

  useEffect(() => {
    fetchFundraiser();
  }, []);

  async function devClearProject(): Promise<void> {
    await fundraiser?.devClearProject();
  }

  async function disconnectCRM(): Promise<void> {
    if (!fundraiser) {
      return;
    }
    await fundraiser?.disconnectCRM();
    await fetchFundraiser();
  }

  return {
    fundraiser,
    /* Consider initial undefined and model fetch/update states */
    isPending: !fundraiser || fundraiser.isPending || fundraiser.isUpdating,
    /* For beta, we will only consider an account connected or not connected */
    state: fundraiser?.get('stripe_connect_account')?.state,
    crm: fundraiser?.get('crm'),
    devClearProject,
    disconnectCRM,
  };
}
